import { useCallback, useEffect, useRef, useState } from "react";
import {
  API_ROUTES,
  MASZYNY,
  MASZYNY_FREE_SPINS,
  MASZYNY_PAYOUTS,
  isValidMaszynyBet,
  maszynyCellKey,
  randomMaszynyGrid,
  type MaszynyPaylineId,
  type MaszynySessionResponse,
  type MaszynySpinRequest,
  type MaszynySpinResponse,
  type MaszynySymbol,
} from "@ziolo/shared";
import { Button, Card } from "@ziolo/ui";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { useSession } from "../session/SessionProvider.js";
import { MaszynyBoard, type MaszynyReelPhase } from "../games/maszyny/MaszynyBoard.js";
import "../styles/maszyny.css";

const IDLE_PHASES: MaszynyReelPhase[] = ["idle", "idle", "idle", "idle", "idle"];

function fillPhases(phase: MaszynyReelPhase): MaszynyReelPhase[] {
  return [phase, phase, phase, phase, phase];
}
const MIN_SPIN_MS = 1600;
const REEL_STOP_STAGGER_MS = 280;
const REEL_DECEL_MS = 700;

type MessageTone = "idle" | "win" | "lose" | "bonus" | "error";

function idleGrid(): MaszynySymbol[][] {
  return randomMaszynyGrid();
}

function clampBet(value: number): number {
  if (!Number.isFinite(value)) return MASZYNY.DEFAULT_BET;
  return Math.min(MASZYNY.MAX_BET, Math.max(MASZYNY.MIN_BET, Math.trunc(value)));
}

function zetonWord(amount: number): string {
  const abs = Math.abs(Math.trunc(amount));
  const n10 = abs % 10;
  const n100 = abs % 100;
  if (n10 === 1 && n100 !== 11) return "żeton";
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return "żetony";
  return "żetonów";
}

function formatLines(result: MaszynySpinResponse): string {
  if (result.winningLines.length === 0) return "";
  return result.winningLines
    .map((line) => `${line.count}× ${line.symbol} na ${line.label}`)
    .join(", ");
}

function resultMessage(result: MaszynySpinResponse): string {
  const parts: string[] = [];
  if (result.winnings > 0) {
    const lines = formatLines(result);
    const win = `Wygrana: ${result.winnings} ${zetonWord(result.winnings)}!`;
    parts.push(lines ? `${win} (${lines})` : win);
  }
  if (result.freeSpinsAwarded > 0) {
    parts.push(`⭐ ${result.freeSpinsAwarded} darmowych spinów!`);
  }
  if (parts.length > 0) return parts.join(" ");
  return "Spróbuj jeszcze raz!";
}

function resultTone(result: MaszynySpinResponse): MessageTone {
  if (result.freeSpinsAwarded > 0) return "bonus";
  if (result.winnings > 0) return "win";
  return "lose";
}

function Paytable() {
  return (
    <div className="maszyny-paytable" data-testid="maszyny-paytable">
      <p className="maszyny-paylines-info">
        5 linii: 3 rzędy, V i odwrócone V · 3/4/5 tego samego symbolu na linii
      </p>
      <ul className="maszyny-paytable__list">
        <li>
          <span>💎</span> {MASZYNY_PAYOUTS["💎"][3]}/{MASZYNY_PAYOUTS["💎"][4]}/{MASZYNY_PAYOUTS["💎"][5]}
        </li>
        <li>
          <span>7️⃣</span> {MASZYNY_PAYOUTS["7️⃣"][3]}/{MASZYNY_PAYOUTS["7️⃣"][4]}/{MASZYNY_PAYOUTS["7️⃣"][5]}
        </li>
        <li>
          <span>🔔</span> {MASZYNY_PAYOUTS["🔔"][3]}/{MASZYNY_PAYOUTS["🔔"][4]}/{MASZYNY_PAYOUTS["🔔"][5]}
        </li>
        <li>
          <span>🍒</span> {MASZYNY_PAYOUTS["🍒"][3]}/{MASZYNY_PAYOUTS["🍒"][4]}/{MASZYNY_PAYOUTS["🍒"][5]}
        </li>
        <li>
          <span>🍋🍇🍉🍊</span> {MASZYNY_PAYOUTS["🍋"][3]}/{MASZYNY_PAYOUTS["🍋"][4]}/{MASZYNY_PAYOUTS["🍋"][5]}
        </li>
      </ul>
      <p className="maszyny-hint">
        Kwoty przy stawce {MASZYNY.BET_UNIT}. Te same żetony co blackjack, ruletka, poker
        i sklep. ⭐ 3/4/5: {MASZYNY_FREE_SPINS[3]}/{MASZYNY_FREE_SPINS[4]}/{MASZYNY_FREE_SPINS[5]}{" "}
        darmowych spinów.
      </p>
    </div>
  );
}

export function MaszynyPage() {
  const { user, restoring } = useSession();
  const [session, setSession] = useState<MaszynySessionResponse | null>(null);
  const [bet, setBet] = useState<number>(MASZYNY.DEFAULT_BET);
  const [grid, setGrid] = useState<MaszynySymbol[][]>(idleGrid);
  const [phases, setPhases] = useState<MaszynyReelPhase[]>(IDLE_PHASES);
  const [winningKeys, setWinningKeys] = useState<ReadonlySet<string>>(new Set());
  const [scatterKeys, setScatterKeys] = useState<ReadonlySet<string>>(new Set());
  const [activeLines, setActiveLines] = useState<MaszynyPaylineId[]>([]);
  const [message, setMessage] = useState("");
  const [tone, setTone] = useState<MessageTone>("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [spinning, setSpinning] = useState(false);
  const [spinWasFree, setSpinWasFree] = useState(false);
  const spinningRef = useRef(false);
  const timers = useRef<number[]>([]);

  const clearTimers = useCallback(() => {
    for (const id of timers.current) window.clearTimeout(id);
    timers.current = [];
  }, []);

  const later = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(fn, ms);
    timers.current.push(id);
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  useEffect(() => {
    if (session?.freeSpinBet) setBet(session.freeSpinBet);
  }, [session?.freeSpinBet]);

  useEffect(() => {
    if (!user) {
      setSession(null);
      return;
    }
    let cancelled = false;
    void apiRequest<MaszynySessionResponse>(API_ROUTES.maszyny.session)
      .then((payload) => {
        if (!cancelled) {
          setSession(payload);
          setLoadError(null);
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) setLoadError(errorMessage(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  function unlock() {
    spinningRef.current = false;
    setSpinning(false);
  }

  async function spin() {
    if (spinningRef.current || spinning || !session) return;
    const stake = bet;
    if (!isValidMaszynyBet(stake)) {
      setTone("error");
      setMessage("Stawka musi być liczbą od 1 do 100.");
      return;
    }
    const free = session.freeSpins > 0;
    if (!free && stake > session.balance) {
      setTone("error");
      setMessage("Za mało żetonów.");
      return;
    }

    spinningRef.current = true;
    clearTimers();
    setSpinning(true);
    setSpinWasFree(free);
    setMessage("");
    setTone("idle");
    setWinningKeys(new Set());
    setScatterKeys(new Set());
    setActiveLines([]);
    setLoadError(null);

    const previous = session;
    setSession({
      ...session,
      balance: free ? session.balance : session.balance - stake,
      freeSpins: free ? session.freeSpins - 1 : session.freeSpins,
    });

    setPhases(fillPhases("accelerating"));
    later(() => {
      setPhases(fillPhases("fast"));
    }, 480);

    const startedAt = Date.now();
    try {
      const result = await apiRequest<MaszynySpinResponse>(API_ROUTES.maszyny.spin, {
        method: "POST",
        body: { bet: stake } satisfies MaszynySpinRequest,
      });
      const wait = Math.max(0, MIN_SPIN_MS - (Date.now() - startedAt));
      later(() => settle(result), wait);
    } catch (cause) {
      clearTimers();
      setPhases(IDLE_PHASES);
      setSession(previous);
      setTone("error");
      setMessage(errorMessage(cause));
      unlock();
    }
  }

  function settle(result: MaszynySpinResponse) {
    for (let reel = 0; reel < MASZYNY.REELS; reel += 1) {
      later(() => {
        setGrid((current) => current.map((column, index) => (index === reel ? (result.grid[reel] ?? column) : column)));
        setPhases((current) => current.map((phase, index) => (index === reel ? "decelerating" : phase)));
      }, reel * REEL_STOP_STAGGER_MS);
      later(() => {
        setPhases((current) => current.map((phase, index) => (index === reel ? "stopping" : phase)));
      }, reel * REEL_STOP_STAGGER_MS + REEL_DECEL_MS);
      later(() => {
        setPhases((current) => current.map((phase, index) => (index === reel ? "idle" : phase)));
      }, reel * REEL_STOP_STAGGER_MS + REEL_DECEL_MS + 280);
    }

    later(() => {
      setGrid(result.grid);
      setSession((current) => ({
        username: current?.username ?? user?.username ?? "",
        balance: result.balance,
        freeSpins: result.freeSpinsRemaining,
        freeSpinBet: result.freeSpinBet,
      }));
      setWinningKeys(new Set(result.winningPositions.map(maszynyCellKey)));
      setScatterKeys(
        result.freeSpinsAwarded > 0
          ? new Set(result.scatterPositions.map(maszynyCellKey))
          : new Set(),
      );
      setActiveLines(result.winningLines.map((line) => line.id));
      setMessage(resultMessage(result));
      setTone(resultTone(result));
      if (result.freeSpinsAwarded > 0) {
        later(() => {
          setMessage(`Zostało ${result.freeSpinsRemaining} darmowych spinów.`);
          setTone("bonus");
        }, 2000);
      }
      unlock();
    }, (MASZYNY.REELS - 1) * REEL_STOP_STAGGER_MS + REEL_DECEL_MS + 400);
  }

  if (restoring) {
    return <p className="hub-hint">Ładowanie…</p>;
  }

  if (!user) {
    return (
      <Card title="Maszyny" icon="🎰">
        <p className="hub-hint">
          Zaloguj się w belce na górze, żeby kręcić bębnami. Żetony są wspólne z kasynem.
        </p>
      </Card>
    );
  }

  const freeSpin = (session?.freeSpins ?? 0) > 0;
  const usingFree = spinning ? spinWasFree : freeSpin;
  const spinLabel = spinning
    ? usingFree
      ? "DARMOWE KRĘCENIE…"
      : "KRĘCENIE…"
    : freeSpin
      ? "DARMOWY SPIN"
      : "KRĘĆ";

  return (
    <div className="maszyny">
      <header className="maszyny__header">
        <div>
          <h2 className="maszyny__title">
            <span className="z-icon" aria-hidden="true">
              🎰
            </span>
            Maszyny
          </h2>
          <p className="maszyny__player" data-testid="maszyny-player">
            Gracz: <strong>{session?.username ?? user.username}</strong>
          </p>
        </div>
        <dl className="maszyny-stats">
          <div className="maszyny-stat">
            <dt>Żetony</dt>
            <dd>
              <strong data-testid="maszyny-balance">{session?.balance ?? "…"}</strong>
              <span> (kasyno)</span>
            </dd>
          </div>
          <div className={`maszyny-stat${freeSpin ? " maszyny-stat--free" : ""}`} data-testid="maszyny-freespins">
            <dt>Darmowe spiny</dt>
            <dd>
              <strong>{session?.freeSpins ?? 0}</strong>
            </dd>
          </div>
        </dl>
      </header>

      {loadError ? <p className="hub-error">{loadError}</p> : null}

      <MaszynyBoard
        grid={grid}
        phases={phases}
        winningKeys={winningKeys}
        scatterKeys={scatterKeys}
        activeLines={activeLines}
      />

      <div className="maszyny-controls">
        <Paytable />
        <div className="maszyny-controls__row">
          <label className="maszyny-bet">
            Stawka
            <button
              type="button"
              className="maszyny-bet__step"
              aria-label="Zmniejsz stawkę"
              disabled={spinning || freeSpin}
              onClick={() => setBet((current) => clampBet(current - 1))}
            >
              −
            </button>
            <input
              type="number"
              min={MASZYNY.MIN_BET}
              max={MASZYNY.MAX_BET}
              value={bet}
              disabled={spinning || freeSpin}
              data-testid="maszyny-bet"
              onChange={(event) => {
                const next = Number.parseInt(event.target.value, 10);
                setBet(Number.isFinite(next) ? next : MASZYNY.DEFAULT_BET);
              }}
              onBlur={() => setBet((current) => clampBet(current))}
            />
            <button
              type="button"
              className="maszyny-bet__step"
              aria-label="Zwiększ stawkę"
              disabled={spinning || freeSpin}
              onClick={() => setBet((current) => clampBet(current + 1))}
            >
              +
            </button>
            żetonów
          </label>
          <Button
            className={`maszyny-spin${usingFree ? " maszyny-spin--free" : ""}`}
            disabled={spinning || !session}
            onClick={() => void spin()}
            data-testid="maszyny-spin"
          >
            {spinLabel}
          </Button>
        </div>
        <p className={`maszyny-message maszyny-message--${tone}`} data-testid="maszyny-message" aria-live="polite">
          {message}
        </p>
      </div>
    </div>
  );
}
